import React, { useState, useEffect } from "react";
import useAdminOTC from "../Hooks/useAdminOTC";
import LoadingSpinner from "../Assets/LoadingSpinner";
import ErrorComponent from "../Assets/ErrorComponent";
import { Product } from "../Types/Types";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

// Utility hook for responsive breakpoints
const useResponsiveItemsPerPage = (): number => {
    const [items, setItems] = useState(3);

    useEffect(() => {
        const updateItems = () => {
            const width = window.innerWidth;
            if (width < 640) {
                setItems(1); // mobile
            } else if (width < 1024) {
                setItems(2); // tablet
            } else {
                setItems(3); // desktop
            }
        };

        updateItems();
        window.addEventListener("resize", updateItems);
        return () => window.removeEventListener("resize", updateItems);
    }, []);

    return items;
};

interface ProductBoxProps {
    product: Product;
    lang: string;
}

const ProductBox: React.FC<ProductBoxProps> = ({ product, lang }) => (
    <Link to={`/product/${product._id}`} className="group">
        <div className="flex-none w-full sm:w-[20rem] md:w-[24rem] h-[22rem] sm:h-[24rem] md:h-[26rem] bg-white rounded-2xl shadow-md border border-gray-100 p-4 sm:p-6 transition-all duration-300 group-hover:-translate-y-1.5 group-hover:shadow-2xl">
            <div className="flex flex-col justify-center items-center h-full text-center">
                <div className="bg-gradient-to-br from-brand-50 to-white p-4 rounded-full mb-4 transition-transform duration-300 group-hover:scale-105">
                    <img
                        src={product.image}
                        alt={product.name?.[lang] || 'Galafarm'}
                        className="h-24 w-24 sm:h-32 sm:w-32 md:h-40 md:w-40 object-contain"
                    />
                </div>
                <h3
                    className="text-lg sm:text-xl font-bold text-brand-gray mb-2 transition-colors duration-200 group-hover:text-brand-600"
                    dangerouslySetInnerHTML={{
                        __html: (product.name[lang] || "").replace(/®/g, '<sup style="font-size:20px;">®</sup>'),
                    }}
                />
                <p className="text-sm sm:text-md text-gray-500 italic">{product.dosage?.[lang] || ""}</p>
            </div>
        </div>
    </Link>
);

interface SimilarProductsProps {
    product: string;
    excludeProductId?: string;
    lang: string;

}

const SimilarProducts: React.FC<SimilarProductsProps> = ({ product, excludeProductId, lang }) => {
    const { t } = useTranslation();

    const itemsPerPage = useResponsiveItemsPerPage();
    const [startIndex, setStartIndex] = useState(0);
    const [direction, setDirection] = useState<"next" | "prev">("next");
    const { products, loading, error } = useAdminOTC(20, product);

    useEffect(() => {
        setStartIndex(0);
    }, [product, excludeProductId, itemsPerPage]);

    const filteredProducts = excludeProductId
        ? products.filter(p => p._id !== excludeProductId)
        : products;

    const visibleProducts = filteredProducts.slice(startIndex, startIndex + itemsPerPage);
    const maxStartIndex = Math.max(0, filteredProducts.length - itemsPerPage);

    const handlePrev = () => {
        setDirection("prev");
        setStartIndex(prev => Math.max(prev - itemsPerPage, 0));
    };

    const handleNext = () => {
        setDirection("next");
        setStartIndex(prev => Math.min(prev + itemsPerPage, maxStartIndex));
    };

    return (
        <section className="py-16 px-4 bg-white border-t border-gray-100">
            <div className="relative max-w-6xl mx-auto">
                <div className="mb-8">
                    <h2 className="text-2xl font-bold text-brand-gray">{t("similarProduct.similar")}</h2>
                    <span className="mt-4 block h-1 w-16 rounded-full bg-brand-400" />
                </div>

                {loading ? (
                    <LoadingSpinner />
                ) : error ? (
                    <ErrorComponent message={error} />
                ) : filteredProducts.length === 0 ? (
                    <p className="text-gray-500">{t("similarProduct.noProducts")}</p>
                ) : (
                    <div className="relative flex items-center gap-3 sm:gap-4">
                        {/* Left Arrow */}
                        <button
                            onClick={handlePrev}
                            disabled={startIndex === 0}
                            aria-label="Previous"
                            className={`z-10 flex h-11 w-11 flex-none items-center justify-center rounded-full border shadow-md transition-all duration-200 ${startIndex === 0
                                ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-300"
                                : "border-gray-200 bg-white text-brand-gray hover:scale-110 hover:border-brand-400 hover:bg-brand-400 hover:text-white"
                                }`}
                        >
                            <FaChevronLeft />
                        </button>

                        {/* Product Boxes — animated track */}
                        <div className="flex-1 overflow-hidden">
                            <div
                                key={startIndex}
                                className={`flex justify-center gap-6 ${direction === "next" ? "animate-slide-in-right" : "animate-slide-in-left"
                                    }`}
                            >
                                {visibleProducts.map(product => (
                                    <ProductBox key={product._id} product={product} lang={lang} />
                                ))}
                            </div>
                        </div>

                        {/* Right Arrow */}
                        <button
                            onClick={handleNext}
                            disabled={startIndex >= maxStartIndex}
                            aria-label="Next"
                            className={`z-10 flex h-11 w-11 flex-none items-center justify-center rounded-full border shadow-md transition-all duration-200 ${startIndex >= maxStartIndex
                                ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-300"
                                : "border-gray-200 bg-white text-brand-gray hover:scale-110 hover:border-brand-400 hover:bg-brand-400 hover:text-white"
                                }`}
                        >
                            <FaChevronRight />
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};

export default SimilarProducts;
